import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import MonthPickerModal from './MonthPickerModal';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

export default function HeroGaugeChart({
  todayExtraction = 0,
  internalLimit = 60,
  complianceLimit = 80,
  unit = 'm³',
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  aggregation = 'Daily',
  onAggregationChange,
  onExport,
  seriesData = [],
}) {
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState('from');

  const monthDisplayLabel = useMemo(() => {
    if (!fromDate || !toDate) return moment().format('MMM YYYY');
    const startM = moment(fromDate);
    const endM = moment(toDate);
    if (startM.format('YYYY-MM') === endM.format('YYYY-MM')) {
      return startM.format('MMM YYYY');
    }
    return `${startM.format('MMM YYYY')} - ${endM.format('MMM YYYY')}`;
  }, [fromDate, toDate]);

  const CIRC = 2 * Math.PI * 88;
  const valNum = Number(todayExtraction) || 0;
  const pct = complianceLimit > 0 ? Math.min(1, Math.max(0, valNum / complianceLimit)) : 0;
  const dashOffset = CIRC * (1 - pct);
  const pctDisplay =
    complianceLimit > 0
      ? `${Math.round((valNum / complianceLimit) * 100)}% compliance limit`
      : 'No compliance limit';

  // Status computation
  let statusText = 'No limit set';
  let statusClass = 'neutral';
  let strokeColor = 'rgba(255,255,255,.3)';

  if (complianceLimit > 0) {
    if (valNum > complianceLimit) {
      statusText = 'Compliance limit exceeded';
      statusClass = 'breach';
      strokeColor = 'var(--breach)';
    } else if (internalLimit > 0 && valNum > internalLimit) {
      statusText = 'Above internal limit';
      statusClass = 'warn';
      strokeColor = 'var(--warn)';
    } else {
      statusText = 'Within compliance';
      statusClass = 'ok';
      strokeColor = '#3aa8e0';
    }
  } else if (internalLimit > 0) {
    if (valNum > internalLimit) {
      statusText = 'Above internal limit';
      statusClass = 'warn';
      strokeColor = 'var(--warn)';
    } else {
      statusText = 'Within internal limit';
      statusClass = 'ok';
      strokeColor = '#3aa8e0';
    }
  } else {
    statusText = 'No limit set';
    statusClass = 'neutral';
    strokeColor = 'rgba(255,255,255,.3)';
  }

  // Real series from backend
  const chartSeries = useMemo(() => {
    if (seriesData && seriesData.length > 0) return seriesData;
    return [];
  }, [seriesData]);

  const datasets = [
    {
      type: 'bar',
      label: 'Extraction',
      data: chartSeries.map((s) => s.value),
      backgroundColor: chartSeries.map((s) => {
        if (complianceLimit > 0 && s.value > complianceLimit) return '#e5484d';
        if (internalLimit > 0 && s.value > internalLimit) return '#e79a12';
        return '#1072bb';
      }),
      borderRadius: 4,
      barThickness: chartSeries.length === 1 ? 36 : chartSeries.length <= 4 ? 24 : 13,
      maxBarThickness: 42,
      order: 2,
    },
    ...(internalLimit > 0
      ? [
          {
            type: 'line',
            label: 'Internal',
            data: chartSeries.map(() => internalLimit),
            borderColor: '#e79a12',
            borderDash: [6, 5],
            borderWidth: 1.5,
            pointRadius: 0,
            order: 0,
          },
        ]
      : []),
    ...(complianceLimit > 0
      ? [
          {
            type: 'line',
            label: 'Compliance',
            data: chartSeries.map(() => complianceLimit),
            borderColor: '#e5484d',
            borderDash: [6, 5],
            borderWidth: 1.5,
            pointRadius: 0,
            order: 0,
          },
        ]
      : []),
  ];

  const chartData = {
    labels: chartSeries.map((s) => s.label),
    datasets,
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0b2e45',
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (c) => `${c.dataset.label}: ${Number(c.raw).toFixed(2)} ${unit}`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: '#eef2f5' },
        ticks: { color: '#8aa1ad', font: { size: 11 } },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#eef2f5' },
        ticks: { color: '#8aa1ad', font: { size: 11 } },
        border: { display: false },
      },
    },
  };

  return (
    <div className="client-card hero" style={{ marginBottom: '16px' }}>
      <div className="gauge-wrap">
        <div className="gauge">
          <svg viewBox="0 0 200 200" aria-hidden="true">
            <circle cx="100" cy="100" r="88" fill="none" stroke="rgba(255,255,255,.15)" strokeWidth="14" />
            <circle
              cx="100"
              cy="100"
              r="88"
              fill="none"
              stroke={strokeColor}
              strokeWidth="14"
              strokeLinecap="round"
              strokeDasharray={CIRC}
              strokeDashoffset={dashOffset}
              transform="rotate(-90 100 100)"
              style={{ transition: 'stroke-dashoffset .7s ease, stroke .3s ease' }}
            />
          </svg>
          <div className="val">
            <b>{valNum.toFixed(2)}</b>
            <small>{unit} today</small>
            <em>{pctDisplay}</em>
          </div>
        </div>
        <div
          className={`hero-status ${statusClass}`}
          style={{
            background:
              statusClass === 'breach'
                ? 'var(--breach-bg)'
                : statusClass === 'warn'
                ? 'var(--warn-bg)'
                : statusClass === 'neutral'
                ? 'rgba(255, 255, 255, 0.16)'
                : 'var(--ok-bg)',
            color:
              statusClass === 'breach'
                ? 'var(--breach)'
                : statusClass === 'warn'
                ? 'var(--warn)'
                : statusClass === 'neutral'
                ? '#ffffff'
                : 'var(--ok)',
            border: statusClass === 'neutral' ? '1px solid rgba(255,255,255,0.25)' : 'none',
          }}
        >
          {statusClass === 'ok' && (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          )}
          {statusClass === 'breach' && (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          )}
          {statusClass === 'warn' && (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
          {statusText}
        </div>
      </div>

      <div className="hero-body">
        <div className="hero-title">
          <h2>Today’s extraction vs limits</h2>
          <div className="graph-ctl">
            {aggregation === 'Daily' ? (
              <>
                <span className="rlab">From</span>
                <div className="dt">
                  <input
                    type="date"
                    className="rng-from"
                    value={fromDate}
                    onChange={(e) => onFromDateChange(e.target.value)}
                  />
                </div>
                <span className="rlab">To</span>
                <div className="dt">
                  <input
                    type="date"
                    className="rng-to"
                    value={toDate}
                    onChange={(e) => onToDateChange(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <>
                <span className="rlab">From</span>
                <div className="dt">
                  <button
                    type="button"
                    className="rng-from month-val-btn"
                    onClick={() => {
                      setPickerTarget('from');
                      setIsMonthPickerOpen(true);
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '38px',
                      padding: '0 16px',
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#0f172a',
                      whiteSpace: 'nowrap',
                      transition: 'border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#94a3b8';
                      e.currentTarget.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.background = '#ffffff';
                    }}
                    title="Click to select month"
                  >
                    {moment(fromDate || undefined).format('MMM YYYY')}
                  </button>
                </div>

                <span className="rlab">To</span>
                <div className="dt">
                  <button
                    type="button"
                    className="rng-to month-val-btn"
                    onClick={() => {
                      setPickerTarget('to');
                      setIsMonthPickerOpen(true);
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '38px',
                      padding: '0 16px',
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#0f172a',
                      whiteSpace: 'nowrap',
                      transition: 'border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#94a3b8';
                      e.currentTarget.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.background = '#ffffff';
                    }}
                    title="Click to select month"
                  >
                    {moment(toDate || undefined).format('MMM YYYY')}
                  </button>
                </div>

                <MonthPickerModal
                  isOpen={isMonthPickerOpen}
                  onClose={() => setIsMonthPickerOpen(false)}
                  onConfirm={({ startDate, endDate }) => {
                    onFromDateChange(startDate);
                    onToDateChange(endDate);
                  }}
                  initialStartDate={fromDate}
                  initialEndDate={toDate}
                  activeField={pickerTarget}
                />
              </>
            )}

            <div className="seg">
              {['Daily', 'Monthly'].map((mode) => (
                <button
                  key={mode}
                  className={aggregation === mode ? 'on' : ''}
                  onClick={() => onAggregationChange(mode)}
                >
                  {mode}
                </button>
              ))}
            </div>
            <button className="btn xls" onClick={onExport}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
              </svg>
              Excel
            </button>
          </div>
        </div>

        <div className="lim-legend">
          <span>
            <i style={{ borderColor: 'var(--warn)' }}></i>
            Internal · <b>{(internalLimit || 0) > 0 ? `${Number(internalLimit).toFixed(2)} ${unit}` : 'Not set'}</b>
          </span>
          <span>
            <i style={{ borderColor: 'var(--breach)' }}></i>
            Compliance · <b>{(complianceLimit || 0) > 0 ? `${Number(complianceLimit).toFixed(2)} ${unit}` : 'Not set'}</b>
          </span>
        </div>

        <div className="chart-box" style={{ height: '242px' }}>
          {chartSeries.length > 0 ? (
            <Chart type="bar" data={chartData} options={chartOptions} />
          ) : (
            <div
              style={{
                display: 'flex',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#8aa1ad',
                fontSize: '13px',
              }}
            >
              No extraction readings recorded for selected date range
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
